import React, { memo, useReducer, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, Drawer, IconButton, styled } from '@material-ui/core';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { StyleSheet, css } from 'aphrodite';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { context } from '../../../../globalState/GlobalState';
import { checkEmailValidity, isValidString } from '../../../../catalog/Validity';
import { checkProductsValidity, checkTicketsValidity, emptyCart, getCart, getCartQuantity, getSuccessURL } from '../../../../catalog/Store';
import Button1 from '../../../uiElements/buttons/Button1';
import AlreadyPurchasedLink from '../../../uiElements/storeComponents/alreadyPurchasedLink/AlreadyPurchasedLink';
import TicketCustomerInfoPopup from '../../../uiElements/ticketsComponents/ticketComponent1/TicketsCustomerInfoPopup';
import NavItemCartTicketItem from './NavItemCartTicketItem';
import { showFeesFromCents, showPriceFromCents, showProfitFromCents, showPlatformFees } from '../../../../catalog/Price';
import { checkoutFromCartAPI } from '../../../apiCalls/StoreAPICalls';
import { getReceiptPath, withQueryParam } from '../../../../catalog/Receipt';
import NavItemCartProductItem from './NavItemCartProductItem';
import './css/CartDrawer1.css';

const initialState = {
    drawer: false,
    openDrawerWhenChangeInPrice: false,
    customerInfoPopup: false,
    customerName: '',
    customerEmail: '',
    prices: {},
    shippingPrices: {},
    checkoutLoadingSpinner: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'reset':
            return { ...initialState };
        case 'resetWithDrawerOpen':
            return { ...initialState, drawer: true };
        case 'openDrawer':
            return { ...state, drawer: true };
        case 'closeDrawer':
            return { ...state, drawer: false };
        case 'openDrawerWhenChangeInPrice':
            return { ...state, openDrawerWhenChangeInPrice: true };
        case 'toggleDrawer':
            return { ...state, drawer: !state.drawer };
        case 'openCustomerInfoPopup':
            return { ...state, customerInfoPopup: true };
        case 'closeCustomerInfoPopup':
            return { ...state, customerInfoPopup: false };
        case 'setCustomerName':
            return { ...state, customerName: action.name };
        case 'setCustomerEmail':
            return { ...state, customerEmail: action.email };
        case 'setPrice':
            return { ...state, prices: { ...state.prices, [action.id]: action.price } };
        case 'setShippingPrice':
            return { ...state, shippingPrices: { ...state.shippingPrices, [action.id]: action.price } };
        case 'setCheckoutLoadingSpinner':
            return { ...state, checkoutLoadingSpinner: action.loadingSpinner };
        default:
            return initialState;
    }
}

const NavItemCart = (props) => {
    const { desktop } = props;
    const [state, setState] = useReducer(reducer, initialState);
    const history = useHistory();
    const { globalState, setGlobalState } = useContext(context);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const checkoutSuccess = params.get('checkoutsuccess');
        if (isValidString(checkoutSuccess)) {
            emptyCart(setGlobalState);
            setState({ type: 'reset' });
            params.delete('checkoutsuccess');
            const remainingSearch = params.toString();
            history.replace(`${window.location.pathname}${remainingSearch.length > 0 ? `?${remainingSearch}` : ''}`);
        } else {
            setGlobalState({ type: 'setCart', cart: getCart() });
            setGlobalState({ type: 'setCartQuantity', quantity: getCartQuantity() });
            setState({ type: 'closeDrawer' });
        }
        setTimeout(() => {
            setState({ type: 'openDrawerWhenChangeInPrice' });
        }, 5000);
    }, []);

    useEffect(() => {
        if (!state.drawer && Object.keys(state.prices).length > 0 && desktop && state.openDrawerWhenChangeInPrice) {
            setState({ type: 'openDrawer' });
        }
    }, [state.prices]);

    const styles = StyleSheet.create({
        navitem: {
            color: isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor} !important` : '#ffffff !important',
            '@media (max-width: 1200px)': {
                // color: 'white',
                marginRight: '80px',
            },
        },
    });

    const StyledBadge = styled(Badge)({
        '& .MuiBadge-badge': {
            color: globalState.websiteStyles.colors.primaryColor,
            backgroundColor: isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor}` : 'white',
        },
    });

    const getTotalPrice = () => {
        let total = 0;
        Object.values(state.prices).forEach((price) => {
            if (price) {
                total += price;
            }
        });
        return total;
    };

    const getShippingPrice = () => {
        let total = 0;
        Object.values(state.shippingPrices).forEach((price) => {
            if (price) {
                total += price;
            }
        });
        return total;
    };

    const getCartItem = (cartItem) => {
        if (cartItem.cartItemType === 'TICKET') {
            return (
                <NavItemCartTicketItem
                    cartItemId={cartItem.cartItemId}
                    ticketId={cartItem.ticketId}
                    quantity={cartItem.quantity}
                    setPrice={(price) => { setState({ type: 'setPrice', id: cartItem.cartItemId, price }); }}
                />
            );
        } if (cartItem.cartItemType === 'PRODUCT') {
            return (
                <NavItemCartProductItem
                    cartItemId={cartItem.cartItemId}
                    productId={cartItem.productId}
                    productOptions={cartItem.productOptions}
                    quantity={cartItem.quantity}
                    setPrice={(price) => { setState({ type: 'setPrice', id: cartItem.cartItemId, price }); }}
                    setShippingPrice={(price) => { setState({ type: 'setShippingPrice', id: cartItem.cartItemId, price }); }}
                />
            );
        }
        return null;
    };

    const checkout = async () => {
        setState({ type: 'setCheckoutLoadingSpinner', loadingSpinner: true });

        let shippable = false;
        let shippingPrice = 0;
        let shippingWeight = 0;

        const { cart } = globalState;
        const finalStripePrices = [];
        const finalProductOptions = {};

        let productsExist = false;
        let ticketsExist = false;
        cart.forEach((cartItem) => {
            if (cartItem.cartItemType === 'PRODUCT') {
                productsExist = true;
            }
            if (cartItem.cartItemType === 'TICKET') {
                ticketsExist = true;
            }
        });

        if (productsExist) {
            if (!checkProductsValidity(globalState)) {
                enqueueSnackbar('Few items in your cart are not available for purchase at the moment', { variant: 'error' });
                emptyCart(setGlobalState);
                setState({ type: 'resetWithDrawerOpen' });
                return;
            }
        }

        if (ticketsExist) {
            if (!checkTicketsValidity(globalState)) {
                enqueueSnackbar('Few items in your cart are not available for purchase at the moment', { variant: 'error' });
                emptyCart(setGlobalState);
                setState({ type: 'resetWithDrawerOpen' });
                return;
            }
        }

        cart.forEach((cartItem) => {
            finalStripePrices.push({ id: cartItem.stripePriceId, quantity: cartItem.quantity, cartItemId: cartItem.cartItemId, shippable: cartItem.shippable, shippingPrice: cartItem.shippingPrice * cartItem.quantity, shippingWeight: cartItem.shippingWeight * cartItem.quantity });
            finalProductOptions[cartItem.cartItemId] = JSON.stringify(cartItem.productOptions);
            shippable = shippable || cartItem.shippable;
            shippingPrice += cartItem.shippingPrice * cartItem.quantity;
            shippingWeight += cartItem.shippingWeight * cartItem.quantity;
        });

        if (getTotalPrice() === 0) {
            if (state.customerInfoPopup) {
                if (!isValidString(state.customerName)) {
                    enqueueSnackbar('Please enter valid customer name', { variant: 'error' });
                    return;
                }
                if (!checkEmailValidity(state.customerEmail, () => {}, enqueueSnackbar)) {
                    enqueueSnackbar('Please enter valid customer email', { variant: 'error' });
                    return;
                }
            } else {
                setState({ type: 'openCustomerInfoPopup' });
                return;
            }
        }

        let redirectUrl = window.location.href;
        if (window.location.href.includes('?checkoutsuccess=true')) {
            redirectUrl = redirectUrl.replaceAll('?checkoutsuccess=true', '');
        }

        const receiptToken = await checkoutFromCartAPI(globalState.stripeAccountId, finalStripePrices, state.customerName, state.customerEmail, finalProductOptions, shippable, shippingPrice, shippingWeight, withQueryParam(getSuccessURL(globalState), 'checkoutsuccess', 'true'), redirectUrl, enqueueSnackbar, setState, setGlobalState);
        if (receiptToken) {
            history.push(getReceiptPath(receiptToken));
        }

        setState({ type: 'setCheckoutLoadingSpinner', loadingSpinner: false });
    };

    return (
        <>
            <div className={`nav-item nav-item-cart ${css(styles.navitem)}`} onClick={() => { setState({ type: 'openDrawer' }); }}>
                <StyledBadge badgeContent={globalState.cartQuantity}>
                    <ShoppingCartIcon />
                </StyledBadge>
            </div>
            <Drawer
                className="cart1-drawer"
                anchor="right"
                open={state.drawer}
                onClose={() => { setState({ type: 'closeDrawer' }); }}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                {/* Option A cart: header with item count, scrolling list of rows, totals + Checkout pinned at the bottom. */}
                {isValidString(globalState.stripeAccountId) ? (
                    <div className="cart1">
                        <div className="cart1-head">
                            <h2 className="cart1-heading alegreya-font-family">
                                Your cart
                                {globalState.cartQuantity > 0 ? <span>{`${globalState.cartQuantity} ${globalState.cartQuantity === 1 ? 'item' : 'items'}`}</span> : null}
                            </h2>
                            <IconButton className="cart1-close" aria-label="Close cart" size="medium" onClick={() => { setState({ type: 'closeDrawer' }); }}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                        <div className="cart1-list">
                            {globalState.cart.length === 0 ? (
                                <div className="cart1-empty">
                                    <p className="cart1-empty-title">Your cart is empty</p>
                                    <p className="cart1-empty-text">Tickets and merch you add will show up here.</p>
                                </div>
                            ) : null}
                            {globalState.cart.map((cartItem) => {
                                return (
                                    <React.Fragment key={cartItem.cartItemId}>
                                        {getCartItem(cartItem)}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        {globalState.cart.length > 0 ? (
                            <div className="cart1-foot">
                                {showPlatformFees ? (
                                    <>
                                        <div className="cart1-sum-row">
                                            <span>Subtotal</span>
                                            <span>{showProfitFromCents(getTotalPrice())}</span>
                                        </div>
                                        <div className="cart1-sum-row">
                                            <span>Platform fee</span>
                                            <span>{showFeesFromCents(getTotalPrice())}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="cart1-sum-row">
                                        <span>Subtotal</span>
                                        <span>{showPriceFromCents(getTotalPrice())}</span>
                                    </div>
                                )}
                                {getShippingPrice() > 0 ? (
                                    <div className="cart1-sum-row">
                                        <span>Shipping &amp; handling</span>
                                        <span>{showPriceFromCents(getShippingPrice())}</span>
                                    </div>
                                ) : null}
                                <div className="cart1-total">
                                    <span>Total</span>
                                    <b>{showPriceFromCents(getTotalPrice() + getShippingPrice())}</b>
                                </div>
                                <Button1
                                    onClick={checkout}
                                    title={`Checkout · ${showPriceFromCents(getTotalPrice() + getShippingPrice())}`}
                                    disabled={globalState.cartQuantity === 0}
                                    backgroundColor={globalState.websiteStyles.colors.primaryColor}
                                    fontColor={isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor}` : 'white'}
                                    fontSize="1rem"
                                    fontFamily="Roboto Condensed, Helvetica, Arial, sans-serif"
                                    borderRadius="12px"
                                    height="52px"
                                    width="100%"
                                    loadingSpinner={state.checkoutLoadingSpinner}
                                />
                                <p className="cart1-secure">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
                                        <rect x="5" y="11" width="14" height="10" rx="2" />
                                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                                    </svg>
                                    Secure checkout with Stripe
                                </p>
                                <AlreadyPurchasedLink />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Drawer>
            <TicketCustomerInfoPopup
                open={state.customerInfoPopup}
                onCancel={() => {
                    setState({ type: 'closeCustomerInfoPopup' });
                    setState({ type: 'setCheckoutLoadingSpinner', loadingSpinner: false });
                }}
                name={state.customerName}
                email={state.customerEmail}
                onNameUpdate={(name) => { setState({ type: 'setCustomerName', name }); }}
                onEmailUpdate={(email) => { setState({ type: 'setCustomerEmail', email }); }}
                checkout={() => {
                    checkout();
                    setState({ type: 'closeCustomerInfoPopup' });
                    emptyCart(setGlobalState);
                    setState({ type: 'reset' });
                }}
            />
        </>
    );
};

NavItemCart.propTypes = {
    desktop: PropTypes.bool.isRequired,
};

export default (memo(NavItemCart));
